import { useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Mail,
  Phone,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { usePermissions } from "@/_core/hooks/usePermissions";

const NONE_SENTINEL = "__none__";

export default function Clients() {
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingKlient, setEditingKlient] = useState<any>(null);
  const [sortField, setSortField] = useState<
    "alias" | "meno" | "priezvisko" | "email" | "telCislo"
  >("alias");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const { t } = useTranslation();
  const {
    canCreateClient,
    canUpdateClient,
    canDeleteClient,
    canListAllClients,
  } = usePermissions();

  // Controlled state for Select fields (can't use uncontrolled with Radix Select)
  const [pohlavie, setPohlavie] = useState<string>(NONE_SENTINEL);
  const [ciel, setCiel] = useState<string>(NONE_SENTINEL);
  const [aktivitaUroven, setAktivitaUroven] = useState<string>(NONE_SENTINEL);

  const { data: klienti, isLoading } = trpc.klient.list.useQuery();
  const createMutation = trpc.klient.create.useMutation();
  const updateMutation = trpc.klient.update.useMutation();
  const deleteMutation = trpc.klient.delete.useMutation();
  const utils = trpc.useUtils();

  const filteredKlienti = useMemo(() => {
    let result =
      klienti?.filter(k =>
        `${k.alias} ${k.meno} ${k.priezvisko}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      ) || [];

    // Sort the filtered results
    result = [...result].sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      // Handle null/undefined values
      if (aValue == null) aValue = "";
      if (bValue == null) bValue = "";

      // For name sorting, we need to compare both first and last names
      if (sortField === "meno") {
        aValue = `${a.meno || ""} ${a.priezvisko || ""}`;
        bValue = `${b.meno || ""} ${b.priezvisko || ""}`;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [klienti, searchTerm, sortField, sortDirection]);

  const openDialog = (klient: any | null) => {
    setEditingKlient(klient);
    setPohlavie(klient?.pohlavie || NONE_SENTINEL);
    setCiel(klient?.ciel || NONE_SENTINEL);
    setAktivitaUroven(klient?.aktivitaUroven || NONE_SENTINEL);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const meno = formData.get("meno") as string;
    const priezvisko = formData.get("priezvisko") as string;
    const email = formData.get("email") as string;
    const telCislo = formData.get("telCislo") as string;
    const datumNarodenia = formData.get("datumNarodenia") as string;
    const vyskaM = formData.get("vyskaM") as string;

    const data = {
      alias: formData.get("alias") as string,
      meno: meno || undefined,
      priezvisko: priezvisko || undefined,
      email: email || undefined,
      telCislo: telCislo || undefined,
      datumNarodenia: datumNarodenia || undefined,
      vyskaM: vyskaM || undefined,
      odberTrening: formData.get("odberTrening") === "on",
      odberStrava: formData.get("odberStrava") === "on",
      odberMeranie: formData.get("odberMeranie") === "on",
      pohlavie: pohlavie !== NONE_SENTINEL ? pohlavie : undefined,
      ciel: ciel !== NONE_SENTINEL ? ciel : undefined,
      aktivitaUroven:
        aktivitaUroven !== NONE_SENTINEL ? aktivitaUroven : undefined,
    };

    try {
      if (editingKlient) {
        await updateMutation.mutateAsync({ id: editingKlient.id, data });
        toast.success(t("clients:messages.clientUpdated"));
      } else {
        await createMutation.mutateAsync(data);
        toast.success(t("clients:messages.clientCreated"));
      }
      utils.klient.list.invalidate();
      setIsDialogOpen(false);
      setEditingKlient(null);
    } catch (error: any) {
      toast.error(error.message || t("common:error"));
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm(t("clients:messages.deleteConfirm"))) return;

    try {
      await deleteMutation.mutateAsync({ id });
      toast.success(t("clients:messages.clientDeleted"));
      utils.klient.list.invalidate();
    } catch (error: any) {
      toast.error(error.message || t("common:error"));
    }
  };

  return (
    <DashboardLayout>
      <div className="container py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold">{t("clients:title")}</h1>
            <p className="text-muted-foreground">{t("clients:subtitle")}</p>
          </div>
          {canCreateClient && (
            <Button onClick={() => openDialog(null)}>
              <Plus className="h-4 w-4 mr-2" />
              {t("clients:addClient")}
            </Button>
          )}
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("clients:searchPlaceholder")}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">
                {t("common:loading")}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => {
                        if (sortField === "alias") {
                          setSortDirection(
                            sortDirection === "asc" ? "desc" : "asc"
                          );
                        } else {
                          setSortField("alias");
                          setSortDirection("asc");
                        }
                      }}
                    >
                      <div className="flex items-center gap-1">
                        {t("clients:table.alias")}
                        <ArrowUpDown className="h-4 w-4" />
                        {sortField === "alias" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => {
                        if (sortField === "meno") {
                          setSortDirection(
                            sortDirection === "asc" ? "desc" : "asc"
                          );
                        } else {
                          setSortField("meno");
                          setSortDirection("asc");
                        }
                      }}
                    >
                      <div className="flex items-center gap-1">
                        {t("clients:table.name")}
                        <ArrowUpDown className="h-4 w-4" />
                        {sortField === "meno" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => {
                        if (sortField === "email" || sortField === "telCislo") {
                          setSortDirection(
                            sortDirection === "asc" ? "desc" : "asc"
                          );
                        } else {
                          setSortField("email");
                          setSortDirection("asc");
                        }
                      }}
                    >
                      <div className="flex items-center gap-1">
                        {t("clients:table.contact")}
                        <ArrowUpDown className="h-4 w-4" />
                        {sortField === "email" &&
                          (sortDirection === "asc" ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          ))}
                      </div>
                    </TableHead>
                    <TableHead>{t("clients:table.subscriptions")}</TableHead>
                    <TableHead className="text-right">
                      {t("clients:table.actions")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredKlienti?.map(klient => (
                    <TableRow key={klient.id}>
                      <TableCell className="font-medium">
                        {klient.alias}
                      </TableCell>
                      <TableCell>
                        {klient.meno} {klient.priezvisko}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1 text-sm">
                          {klient.email && (
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Mail className="h-3 w-3" />
                              {klient.email}
                            </div>
                          )}
                          {klient.telCislo && (
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Phone className="h-3 w-3" />
                              {klient.telCislo}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          {klient.odberTrening && (
                            <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                              {t("clients:subscriptions.training")}
                            </span>
                          )}
                          {klient.odberStrava && (
                            <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                              {t("clients:subscriptions.nutrition")}
                            </span>
                          )}
                          {klient.odberMeranie && (
                            <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                              {t("clients:subscriptions.measurements")}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {canUpdateClient && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDialog(klient)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {canDeleteClient && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(klient.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingKlient
                  ? t("clients:dialog.editClient")
                  : t("clients:dialog.addClient")}
              </DialogTitle>
              <DialogDescription>
                {editingKlient
                  ? t("clients:dialog.updateClientInfo")
                  : t("clients:dialog.addNewClient")}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4 py-4">
                {/* Basic info */}
                <div className="space-y-2">
                  <Label htmlFor="alias">
                    {t("clients:dialog.aliasRequired")}
                  </Label>
                  <Input
                    id="alias"
                    name="alias"
                    defaultValue={editingKlient?.alias}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meno">{t("clients:dialog.firstName")}</Label>
                  <Input
                    id="meno"
                    name="meno"
                    defaultValue={editingKlient?.meno}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="priezvisko">
                    {t("clients:dialog.lastName")}
                  </Label>
                  <Input
                    id="priezvisko"
                    name="priezvisko"
                    defaultValue={editingKlient?.priezvisko}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="datumNarodenia">
                    {t("clients:dialog.birthDate")}
                  </Label>
                  <Input
                    id="datumNarodenia"
                    name="datumNarodenia"
                    type="date"
                    defaultValue={editingKlient?.datumNarodenia}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vyskaM">{t("clients:dialog.heightM")}</Label>
                  <Input
                    id="vyskaM"
                    name="vyskaM"
                    type="number"
                    step="0.01"
                    defaultValue={editingKlient?.vyskaM}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{t("clients:dialog.email")}</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    defaultValue={editingKlient?.email}
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="telCislo">
                    {t("clients:dialog.phoneNumber")}
                  </Label>
                  <Input
                    id="telCislo"
                    name="telCislo"
                    defaultValue={editingKlient?.telCislo}
                  />
                </div>

                {/* ── TDEE / Goal settings ─────────────────────────────── */}
                <div className="col-span-2 pt-2 border-t">
                  <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    {t(
                      "clients:dialog.tdeeSettings",
                      "Ciele a aktivita (TDEE)"
                    )}
                  </p>
                </div>

                {/* Pohlavie */}
                <div className="space-y-2">
                  <Label>{t("clients:dialog.pohlavie", "Pohlavie")}</Label>
                  <Select value={pohlavie} onValueChange={setPohlavie}>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t(
                          "clients:dialog.selectPohlavie",
                          "Vybrať…"
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE_SENTINEL}>—</SelectItem>
                      <SelectItem value="M">
                        {t("clients:pohlavie.M", "Muž")}
                      </SelectItem>
                      <SelectItem value="F">
                        {t("clients:pohlavie.F", "Žena")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Ciel */}
                <div className="space-y-2">
                  <Label>{t("clients:dialog.ciel", "Cieľ")}</Label>
                  <Select value={ciel} onValueChange={setCiel}>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t("clients:dialog.selectCiel", "Vybrať…")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE_SENTINEL}>—</SelectItem>
                      <SelectItem value="chudnutie">
                        {t("clients:ciel.chudnutie", "Chudnutie")}
                      </SelectItem>
                      <SelectItem value="udrzanie">
                        {t("clients:ciel.udrzanie", "Udržanie váhy")}
                      </SelectItem>
                      <SelectItem value="naberanie">
                        {t("clients:ciel.naberanie", "Naberanie svalov")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Aktivita uroven */}
                <div className="space-y-2 col-span-2">
                  <Label>
                    {t("clients:dialog.aktivitaUroven", "Úroveň aktivity")}
                  </Label>
                  <Select
                    value={aktivitaUroven}
                    onValueChange={setAktivitaUroven}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t(
                          "clients:dialog.selectAktivita",
                          "Vybrať…"
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE_SENTINEL}>—</SelectItem>
                      <SelectItem value="sedavy">
                        {t("clients:aktivita.sedavy", "Sedavý (×1.2)")}
                      </SelectItem>
                      <SelectItem value="lahky">
                        {t("clients:aktivita.lahky", "Ľahká aktivita (×1.375)")}
                      </SelectItem>
                      <SelectItem value="stredny">
                        {t(
                          "clients:aktivita.stredny",
                          "Stredná aktivita (×1.55)"
                        )}
                      </SelectItem>
                      <SelectItem value="aktivny">
                        {t("clients:aktivita.aktivny", "Aktívny (×1.725)")}
                      </SelectItem>
                      <SelectItem value="velmi_aktivny">
                        {t(
                          "clients:aktivita.velmi_aktivny",
                          "Veľmi aktívny (×1.9)"
                        )}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Subscriptions */}
                <div className="col-span-2 pt-2 border-t">
                  <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    {t("clients:dialog.subscriptions", "Predplatné")}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="odberTrening"
                    name="odberTrening"
                    defaultChecked={editingKlient?.odberTrening}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="odberTrening">
                    {t("clients:dialog.trainingSubscription")}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="odberStrava"
                    name="odberStrava"
                    defaultChecked={editingKlient?.odberStrava}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="odberStrava">
                    {t("clients:dialog.nutritionSubscription")}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="odberMeranie"
                    name="odberMeranie"
                    defaultChecked={editingKlient?.odberMeranie}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="odberMeranie">
                    {t("clients:dialog.measurementsSubscription")}
                  </Label>
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                >
                  {t("common:cancel")}
                </Button>
                <Button type="submit">
                  {editingKlient ? t("common:update") : t("common:create")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
